/* eslint-disable react-hooks/exhaustive-deps */
// ░█▀▀▄ ░█▀▀▀█ 　 ░█▄─░█ ░█▀▀▀█ ▀▀█▀▀ 　 ░█▀▀▀ ░█▀▀▄ ▀█▀ ▀▀█▀▀
// ░█─░█ ░█──░█ 　 ░█░█░█ ░█──░█ ─░█── 　 ░█▀▀▀ ░█─░█ ░█─ ─░█──
// ░█▄▄▀ ░█▄▄▄█ 　 ░█──▀█ ░█▄▄▄█ ─░█── 　 ░█▄▄▄ ░█▄▄▀ ▄█▄ ─░█──
// The component is made to be used flexibly. But if you REALLY MUST update edit the component,
// please add `-custom` on the change log file (e.g. `1.0.0-custom.md`) and log the changes

// ========================================================================================================================================
// Hooks
// ========================================================================================================================================

import { useRouter } from "next/router"
import {
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react"
import { useInView } from "react-intersection-observer"
import { fetchAPI } from "lib/api"

// ==========================================================================
// Scroll Anim
// ==========================================================================

/**
 *
 * @param {*} animation
 * @returns
 *
 * e.g .fadeInUp, .fadeInDown, etc.
 * Please check base/animations.scss for complete list of animations
 */

export const useScrollAnim = (
  animation = "fadeInUp",
  threshold = [0.125, 0.5, 0.75]
) => {
  const [ref, inView] = useInView({
    threshold,
    triggerOnce: true,
  })

  const [animate, setAnimate] = useState(() => ({
    text: `scroll-${animation}`,
    hasSet: false,
  }))

  const anim = useCallback(
    (delay, replaceAnim = null) => {
      if (replaceAnim) {
        return animate.hasSet
          ? `scroll-${replaceAnim} ${replaceAnim} d${delay}`
          : `scroll-${replaceAnim}`
      }
      return animate.hasSet
        ? `${animate.text} ${animation} d${delay}`
        : animate.text
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [animate]
  )

  useEffect(() => {
    if (inView) {
      setAnimate((prevState) => ({
        ...prevState,
        hasSet: true,
      }))
    }
  }, [inView])

  return [ref, anim, inView]
}

export const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect

export const useCustomNavSwiper = () => {
  const swiperRef = useRef(null)
  const [index, setIndex] = useState(swiperRef?.current?.swiper?.realIndex)
  const handlePrev = useCallback(() => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slidePrev()
      setIndex(swiperRef?.current?.swiper?.realIndex)
    } else if (swiperRef?.current) {
      swiperRef?.current?.slidePrev()
      setIndex(swiperRef?.current?.realIndex)
    }
    return null
  }, [])

  const handleNext = useCallback(() => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideNext()
      setIndex(swiperRef?.current?.swiper?.realIndex)
    } else if (swiperRef?.current) {
      swiperRef?.current?.slideNext()
      setIndex(swiperRef?.current?.realIndex)
    }
    return null
  }, [])
  return [swiperRef, handlePrev, handleNext, index || 0]
}

/**
 *
 * @param {*} query
 * import from lib/query. ex: Q_HOME_PAGE
 * @param {object} variables
 * ex: { page: 1, first: 10 }
 * @returns {object} data
 * @returns {boolean} loading
 * @returns {object} error
 *
 */

export function useFetchAPI(query, variables) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [fetched, setFetched] = useState(false)

  const fetchData = async () => {
    try {
      const result = await fetchAPI(query, { variables })
      setData(result)
      setLoading(false)
      setFetched(true)
    } catch (err) {
      setError(err)
      setLoading(false)
      setFetched(true)
    }
  }

  const refetch = async (newVariables) => {
    // try {
    //   const result = await fetchAPI(query, {
    //     variables: newVariables || variables,
    //   })
    //   setData(result)
    //   setLoading(false)
    //   setFetched(true)
    // } catch (err) {
    //   setError(err)
    //   setLoading(false)
    //   setFetched(true)
    // }
    setFetched(false)
    setLoading(true)
  }

  useEffect(() => {
    if (!fetched) {
      fetchData()
    }
  }, [query, variables, fetched])

  return { data, loading, error, refetch }
}
